import React, { useState, useRef, useCallback } from 'react';
import AiSuggestionPanel from './AiSuggestionPanel';
import api from '../../lib/api';
import { aiAPI } from '../../lib/api';

/**
 * JournalAssistant – appears on the Journal Entry form.
 * Suggests entry structures based on the description being typed.
 *
 * Props:
 *  - description: string – current journal entry description
 *  - existingLines: array – current journal lines
 *  - onApplyEntry: (lines) => void – callback to populate lines
 *  - onSetRecurring: () => void – callback to enable recurring toggle
 */
export default function JournalAssistant({ description = '', existingLines = [], onApplyEntry, onSetRecurring }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastQuery = useRef('');
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(async (desc) => {
    if (desc.length < 5 || desc === lastQuery.current) return;
    lastQuery.current = desc;
    setLoading(true);

    try {
      const res = await api.post('/ai/suggestions', {
        context_type: 'journal',
        payload: {
          description: desc,
          current_lines: existingLines.map(l => ({
            account: l.account_name,
            debit: l.debit,
            credit: l.credit,
          })),
        },
      });
      setSuggestions(res.data || []);
    } catch {
      setSuggestions([]);
    }
    setLoading(false);
  }, [existingLines]);

  // Debounced trigger – fires 1.5s after user stops typing
  const triggerAnalysis = useCallback((desc) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(desc), 1500);
  }, [fetchSuggestions]);

  // Call this from parent when description changes
  React.useEffect(() => {
    if (description.length >= 5) {
      triggerAnalysis(description);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [description, triggerAnalysis]);

  const handleAccept = (suggestion) => {
    // Store preference for learning
    aiAPI.acceptSuggestion('journal', description, suggestion.text, suggestion.data).catch(() => {});

    if (suggestion.action === 'apply_entry' && suggestion.data?.lines && onApplyEntry) {
      onApplyEntry(suggestion.data.lines);
    } else if (suggestion.action === 'set_recurring' && onSetRecurring) {
      onSetRecurring();
    }
  };

  return (
    <AiSuggestionPanel
      title="AI Suggestion"
      suggestions={suggestions}
      loading={loading}
      onAccept={handleAccept}
      onDismiss={() => { setSuggestions([]); lastQuery.current = '___dismissed___'; }}
      className="mb-4"
    />
  );
}
